import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  KnowledgeCategories,
  type AgentPhase,
  type KnowledgeCategory,
  type UpdateKnowledgeEntryRequest,
} from '@resolve-ai/shared';
import { knowledgeApi } from '../lib/knowledge-api';
import { describeKnowledgeUpload } from '../lib/knowledge-upload';
import type { KnowledgeEntryResponse } from '@resolve-ai/shared';
import { useAuth } from '../contexts/AuthContext';

type ModalType = 'create' | 'edit' | 'delete' | null;

export interface KnowledgeEntryFormData {
  title: string;
  description: string;
  body: string;
  category: KnowledgeCategory;
  phases: AgentPhase[];
  pinned: boolean;
  /** A newly selected file: for create, the entry's attachment; for edit, a replacement for the entry's existing attachment. */
  file: File | null;
}

const EMPTY_FORM: KnowledgeEntryFormData = {
  title: '',
  description: '',
  body: '',
  category: KnowledgeCategories.CONVENTION,
  phases: [],
  pinned: false,
  file: null,
};

/**
 * Creates a text entry via the plain JSON endpoint, or - when a file is
 * selected - runs the 3-step direct-to-S3 upload flow (mint a presigned
 * URL, PUT the file, then register the entry) before falling back to
 * inspecting the result.
 */
async function createEntry(
  projectId: string,
  data: KnowledgeEntryFormData
): Promise<KnowledgeEntryResponse> {
  if (!data.file) {
    return knowledgeApi.create(projectId, data);
  }

  const file = data.file;
  const upload = describeKnowledgeUpload(file);
  const { entryId, uploadUrl, key } = await knowledgeApi.getUploadUrl(projectId, upload);

  await knowledgeApi.uploadToS3(uploadUrl, file, upload.contentType);

  return knowledgeApi.createFileEntry(projectId, {
    entryId,
    title: data.title,
    description: data.description,
    body: data.body.trim() || undefined,
    category: data.category,
    phases: data.phases,
    ...upload,
    key,
  });
}

/**
 * Updates a knowledge entry's fields, first replacing its attachment via
 * the same 3-step direct-to-S3 flow when a new file was selected. The two
 * are separate API calls - PATCH only ever touches text fields, and never
 * accepts an attachment.
 */
async function updateEntry(
  projectId: string,
  entryId: string,
  data: KnowledgeEntryFormData
): Promise<KnowledgeEntryResponse> {
  if (data.file) {
    const file = data.file;
    const upload = describeKnowledgeUpload(file);
    const { uploadUrl, key } = await knowledgeApi.getReplaceUploadUrl(projectId, entryId, upload);
    await knowledgeApi.uploadToS3(uploadUrl, file, upload.contentType);
    await knowledgeApi.replaceAttachment(projectId, entryId, { ...upload, key });
  }

  const patch: UpdateKnowledgeEntryRequest = {
    title: data.title,
    description: data.description,
    body: data.body,
    category: data.category,
    phases: data.phases,
    pinned: data.pinned,
  };
  return knowledgeApi.update(projectId, entryId, patch);
}

export function useKnowledgeBasePage() {
  const { id: projectId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  }>({ isOpen: false, message: '', variant: 'info' });

  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntryResponse | null>(null);
  const [form, setForm] = useState<KnowledgeEntryFormData>(EMPTY_FORM);

  const showToast = (message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({ isOpen: true, message, variant });
  };

  const closeToast = () => setToast((current) => ({ ...current, isOpen: false }));

  const closeModal = () => {
    setActiveModal(null);
    setSelectedEntry(null);
    setForm(EMPTY_FORM);
  };

  const entriesQuery = useQuery<KnowledgeEntryResponse[]>({
    queryKey: ['knowledge-entries', projectId],
    queryFn: () => knowledgeApi.list(projectId!),
    enabled: Boolean(projectId),
  });

  const invalidateEntries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['knowledge-entries', projectId] });
  };

  const createMutation = useMutation({
    mutationFn: (data: KnowledgeEntryFormData) => createEntry(projectId!, data),
    onSuccess: async () => {
      closeModal();
      await invalidateEntries();
      showToast('Knowledge entry created', 'success');
    },
    onError: (error) => {
      console.error('Failed to create knowledge entry:', error);
      showToast('Failed to create knowledge entry', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: KnowledgeEntryFormData) => updateEntry(projectId!, selectedEntry!.id, data),
    onSuccess: async () => {
      closeModal();
      await invalidateEntries();
      showToast('Knowledge entry updated', 'success');
    },
    onError: (error) => {
      console.error('Failed to update knowledge entry:', error);
      showToast('Failed to update knowledge entry', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: string) => knowledgeApi.delete(projectId!, entryId),
    onSuccess: async () => {
      closeModal();
      await invalidateEntries();
      showToast('Knowledge entry deleted', 'success');
    },
    onError: (error) => {
      console.error('Failed to delete knowledge entry:', error);
      showToast('Failed to delete knowledge entry', 'error');
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: (entry: KnowledgeEntryResponse) =>
      knowledgeApi.update(projectId!, entry.id, { enabled: !entry.enabled }),
    onSuccess: async () => {
      await invalidateEntries();
    },
    onError: (error) => {
      console.error('Failed to toggle knowledge entry:', error);
      showToast('Failed to update knowledge entry', 'error');
    },
  });

  const importMutation = useMutation({
    mutationFn: () => knowledgeApi.importFromRepos(projectId!),
    onSuccess: async (summary) => {
      await invalidateEntries();
      const total = summary.created + summary.updated;
      showToast(
        total > 0
          ? `Imported ${summary.created} new and updated ${summary.updated} entries from your repositories`
          : 'No new guidance found in your connected repositories',
        total > 0 ? 'success' : 'info'
      );
    },
    onError: (error) => {
      console.error('Failed to import knowledge base:', error);
      showToast('Failed to import from repositories', 'error');
    },
  });

  const isFormValid =
    form.title.trim() !== '' &&
    form.description.trim() !== '' &&
    (form.body.trim() !== '' || form.file !== null);

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setActiveModal('create');
  };

  const openEditModal = (entry: KnowledgeEntryResponse) => {
    setSelectedEntry(entry);
    setForm({
      title: entry.title,
      description: entry.description,
      body: entry.body,
      category: entry.category,
      phases: entry.phases,
      pinned: entry.pinned,
      file: null,
    });
    setActiveModal('edit');
  };

  const openDeleteModal = (entry: KnowledgeEntryResponse) => {
    setSelectedEntry(entry);
    setActiveModal('delete');
  };

  const handleSave = () => {
    if (!isFormValid) return;
    if (activeModal === 'create') {
      createMutation.mutate(form);
    } else if (activeModal === 'edit') {
      updateMutation.mutate(form);
    }
  };

  const handleDelete = () => {
    if (!selectedEntry) return;
    deleteMutation.mutate(selectedEntry.id);
  };

  const handleToggleEnabled = (entry: KnowledgeEntryResponse) => {
    toggleEnabledMutation.mutate(entry);
  };

  const handleImport = () => {
    if (importMutation.isPending) return;
    importMutation.mutate();
  };

  return {
    query: {
      entries: entriesQuery.data ?? [],
      isLoading: entriesQuery.isLoading,
      hasError: entriesQuery.isError && !entriesQuery.data,
      isAdmin,
    },
    modal: {
      active: activeModal,
      selectedEntry,
      close: closeModal,
    },
    form: {
      data: form,
      setData: setForm,
      isValid: isFormValid,
    },
    actions: {
      openCreateModal,
      openEditModal,
      openDeleteModal,
      save: handleSave,
      delete: handleDelete,
      toggleEnabled: handleToggleEnabled,
      importFromRepos: handleImport,
    },
    mutations: {
      isSaving: createMutation.isPending || updateMutation.isPending,
      isDeleting: deleteMutation.isPending,
      isImporting: importMutation.isPending,
    },
    toast: {
      state: toast,
      close: closeToast,
    },
  };
}
